import React from "react";
import { Card, Box, Typography, Grid, Divider, Stack } from "@mui/material";

const chunkBreakPairs = (arr) => {
  const rows = [];
  for (let i = 0; i < arr.length; i += 2) rows.push(arr.slice(i, i + 2));
  return rows;
};
const breakShort = (t) => (t === "breakOut" ? "Out" : t === "breakIn" ? "In" : "·");

const buildSessions = ({ sessions, checkIns, checkOuts, inTime, outTime }) => {
  if (Array.isArray(sessions) && sessions.length > 0) {
    return sessions.map((s, i) => ({
      idx: s.sessionNumber || i + 1,
      inTime: s.checkIn?.time || s.inTime || "—",
      outTime: s.checkOut?.time || s.outTime || "—",
      hours: s.hours,
      status: s.checkIn?.status || s.status,
    }));
  }

  const maxLen = Math.max(checkIns?.length || 0, checkOuts?.length || 0);
  if (maxLen > 0) {
    const list = [];
    for (let i = 0; i < maxLen; i++) {
      list.push({
        idx: i + 1,
        inTime: checkIns?.[i]?.time || "—",
        outTime: checkOuts?.[i]?.time || "—",
        status: checkIns?.[i]?.status,
      });
    }
    return list;
  }

  if (inTime || outTime) {
    return [{ idx: 1, inTime: inTime || "—", outTime: outTime || "—" }];
  }

  return [];
};

const DailyLogCard = ({
  title,
  inName = "Check In",
  outName = "Check Out",
  inTime,
  outTime,
  sessions,
  checkIns,
  checkOuts,
  /** Optional `{ type, time }[]` — when set, shows grouped breaks instead of two big lines */
  breaks,
  breakInTime,
  breakOutTime,
  totalLate,
  status,
}) => {
  const sessionList = buildSessions({ sessions, checkIns, checkOuts, inTime, outTime });
  const multiSession = sessionList.length > 1;

  return (
    <Card
      sx={{
        border: "1.5px solid #2E7D32",
        borderRadius: "16px",
        boxShadow: "none",
        p: 2,
        bgcolor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box textAlign="center" width="100%">
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          {title}
        </Typography>

        {sessionList.length === 0 ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            No attendance recorded today
          </Typography>
        ) : multiSession ? (
          <Stack spacing={1} width="100%" mb={2}>
            {sessionList.map((s) => (
              <Box
                key={s.idx}
                sx={{
                  bgcolor: "#F1F8F4",
                  border: "1px solid #C8E6C9",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={0.5}
                >
                  <Typography variant="caption" fontWeight={700} color="#2E7D32">
                    Session {s.idx}
                  </Typography>
                  {s.status && (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color={s.status === "late" ? "#C62828" : "#2E7D32"}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {s.status === "onTime" ? "On Time" : s.status}
                    </Typography>
                  )}
                </Stack>
                <Grid container>
                  <Grid item xs={6} textAlign="center">
                    <Typography variant="caption" color="#388e3c" fontWeight={600} display="block">
                      {inName}
                    </Typography>
                    <Typography variant="h6" color="#757575" fontWeight={600}>
                      {s.inTime}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="center">
                    <Typography variant="caption" color="#388e3c" fontWeight={600} display="block">
                      {outName}
                    </Typography>
                    <Typography variant="h6" color="#757575" fontWeight={600}>
                      {s.outTime}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>
        ) : (
          <Grid container justifyContent="space-between" alignItems="center" mb={2}>
            <Grid item xs={6} textAlign="center">
              <Typography variant="body2" color="#388e3c" fontWeight={500}>
                {inName}
              </Typography>
              <Typography variant="h4" color="#757575" fontWeight={600}>
                {sessionList[0].inTime}
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign="center">
              <Typography variant="body2" color="#388e3c" fontWeight={500}>
                {outName}
              </Typography>
              <Typography variant="h4" color="#757575" fontWeight={600}>
                {sessionList[0].outTime}
              </Typography>
            </Grid>
          </Grid>
        )}

        <Divider sx={{ my: 1, borderColor: "#2E7D32" }} />

        {Array.isArray(breaks) && breaks.length > 0 ? (
          <Stack spacing={1} width="100%" mb={1}>
            {chunkBreakPairs(breaks).map((pair, i) => (
              <Box
                key={i}
                sx={{
                  bgcolor: "#F1F8F4",
                  border: "1px solid #C8E6C9",
                  borderRadius: "10px",
                  px: 1.25,
                  py: 0.85,
                }}
              >
                <Typography variant="caption" fontWeight={700} color="#2E7D32">
                  Break {i + 1}
                </Typography>
                <Stack direction="row" justifyContent="space-around" flexWrap="wrap" gap={1} mt={0.35}>
                  {pair.map((b, j) => (
                    <Box key={j} textAlign="center" minWidth={56}>
                      <Typography variant="caption" color="#388e3c" fontWeight={600} display="block">
                        {breakShort(b.type)}
                      </Typography>
                      <Typography variant="body1" color="#757575" fontWeight={600}>
                        {b.time}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <Grid container justifyContent="space-between" alignItems="center" mb={2}>
            <Grid item xs={6} textAlign="center">
              <Typography variant="body2" color="#388e3c" fontWeight={500}>
                Break In
              </Typography>
              <Typography variant="h4" color="#757575" fontWeight={600}>
                {breakInTime ?? "—"}
              </Typography>
            </Grid>
            <Grid item xs={6} textAlign="center">
              <Typography variant="body2" color="#388e3c" fontWeight={500}>
                Break Out
              </Typography>
              <Typography variant="h4" color="#757575" fontWeight={600}>
                {breakOutTime ?? "—"}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
    </Card>
  );
};

export default DailyLogCard;
